import { recalcItem } from './orderItemCalc';
import type { FulfillmentMode, OrderItem } from '../types';

/**
 * Dòng lẻ ĐƯỢC PHÉP gộp với dòng lẻ khác cùng biến thể.
 *
 * Loại trừ có chủ đích:
 *  - `comboGroupId` — đang trong cụm combo, gộp là phá bất biến
 *    `quantity == comboUnitQty × comboUnitCount`.
 *  - `trackingMode !== 'quantity'` / `specificationCode` — hàng serial, mỗi dòng
 *    là một mã cụ thể, không có khái niệm cộng dồn.
 *  - `is_gift` / `flash_sale_id` — số lượng và giá do khuyến mãi khoá; gộp vào
 *    dòng thường sẽ làm engine khuyến mãi tính lại sai.
 *  - `hasPriceOverride` — giá do NVKD sửa tay cho riêng dòng đó.
 */
function isMergeable(item: OrderItem): boolean {
  return (
    item.trackingMode === 'quantity' &&
    !item.comboGroupId &&
    !item.specificationCode &&
    !item.is_gift &&
    !item.flash_sale_id &&
    !item.hasPriceOverride &&
    // TCR-2614: dòng "Tách dòng" phải sống riêng dù giá/cờ override đã bị tính lại.
    !item.splitLine
  );
}

/**
 * Khoá gộp. Kho và giá nằm TRONG khoá: gộp hai dòng lệch kho làm xuất sai kho
 * (tồn lệch), gộp hai dòng lệch giá làm đổi số tiền khách trả trong im lặng.
 */
function mergeKey(item: OrderItem): string {
  return [item.variantId, item.warehouseId ?? '', item.price, item.discountPercent].join('|');
}

/**
 * TCR-983: gộp các dòng LẺ trùng sản phẩm thành một dòng.
 *
 * Tách cụm combo trả từng thành phần về dòng lẻ mà không nhìn xem giỏ đã có dòng
 * lẻ cùng biến thể chưa (`splitComboGroup` → `toLooseLine`), `setComboGroupCount`
 * cũng đẩy phần dư thành dòng mới. Kết quả: hai dòng "NƯỚC HOA ... 10" và
 * "NƯỚC HOA ... 1" nằm cạnh nhau thay vì một dòng 11.
 *
 * Hàm thuần. Dòng ĐẦU TIÊN của mỗi khoá sống sót — giữ nguyên `id` và vị trí
 * trong giỏ — và cộng dồn `quantity` của các dòng sau, rồi chạy lại `recalcItem`
 * để `totalAmount` khớp `fulfillmentMode`.
 *
 * CHỈ gọi ở luồng tạo đơn POS. Màn sửa đơn map dòng về order item ĐÃ LƯU theo
 * `id`; gộp mất một `id` là BE không biết dòng đó cần xoá.
 */
export function mergeLooseDuplicates(
  items: OrderItem[],
  fulfillmentMode: FulfillmentMode,
): OrderItem[] {
  /** index trong `result` của dòng đại diện cho mỗi khoá gộp. */
  const survivorIndexByKey = new Map<string, number>();
  const result: OrderItem[] = [];
  let merged = false;

  for (const item of items) {
    if (!isMergeable(item)) {
      result.push(item);
      continue;
    }

    const key = mergeKey(item);
    const survivorIndex = survivorIndexByKey.get(key);
    if (survivorIndex === undefined) {
      survivorIndexByKey.set(key, result.length);
      result.push(item);
      continue;
    }

    const survivor = result[survivorIndex];
    // `splitComboGroupId` (vết của cụm VỪA rời) KHÔNG nằm trong khoá gộp và
    // không chặn gộp, kể cả khi hai dòng mang hai vết khác nhau — tách hai cụm
    // combo liên tiếp là ĐÚNG ca lỗi TCR-983 mô tả. Trường này chỉ được đọc ở
    // `Orders/edit.tsx` (gửi `clear_combo` cho dòng đã lưu), nơi hàm này không
    // bao giờ chạy: xem chú thích "CHỈ gọi ở luồng tạo đơn POS" bên trên.
    result[survivorIndex] = recalcItem(
      {
        ...survivor,
        quantity: survivor.quantity + item.quantity,
        splitComboGroupId: survivor.splitComboGroupId ?? item.splitComboGroupId,
      },
      fulfillmentMode,
    );
    merged = true;
  }

  // Giữ tham chiếu cũ khi không gộp gì — tránh render lại giỏ vô cớ.
  return merged ? result : items;
}
